import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Settings, SessionType, SessionState } from '../types';
import { getNextSessionId, clearCurrentSession } from '../utils/sessionUtils.ts';
import { Storage } from '../utils/storage.ts';
import { ensureNotificationPermission } from '../utils/audioUtils.ts';
import { addSessionToLog } from '../utils/sessionLog.ts';

type RecoveredSession =
  | { kind: 'active'; timeLeft: number; tab: SessionType; sessionId: string; sessionStartTime: number }
  | { kind: 'completed'; nextTab: SessionType }
  | null;

function getDurationForType(sessionType: SessionType, settings: Settings): number {
  switch (sessionType) {
    case 'pomodoro':   return settings.workDuration * 60;
    case 'shortBreak': return settings.shortBreakDuration * 60;
    case 'longBreak':  return settings.longBreakDuration * 60;
  }
}

function recoverSession(settings: Settings): RecoveredSession {
  const saved = Storage.read<SessionState | null>('kf-session-state', null);
  if (!saved || !saved.wallClockStart || !saved.timeLeft || !saved.sessionType) return null;

  const elapsed = Math.floor((Date.now() - saved.wallClockStart) / 1000);
  const remaining = saved.timeLeft - elapsed;

  if (remaining <= 0) {
    addSessionToLog(
      saved.currentSessionId,
      saved.sessionType,
      saved.startTime,
      Date.now(),
      getDurationForType(saved.sessionType, settings),
      saved.timeLeft,
      'completed'
    );
    Storage.write('kf-session-state', null);
    const nextTab: SessionType = saved.sessionType === 'pomodoro' ? 'shortBreak' : 'pomodoro';
    return { kind: 'completed', nextTab };
  }

  return {
    kind: 'active',
    timeLeft: remaining,
    tab: saved.sessionType,
    sessionId: saved.currentSessionId,
    sessionStartTime: saved.startTime,
  };
}

interface TimerContextType {
  timeLeft: number;
  isActive: boolean;
  initialTimeLeft: number;
  sessionStarted: boolean;
  currentSessionId: string | null;
  sessionStartTime: number | null;
  pomodoroCount: number;
  currentTab: string;
  handleStartStop: () => void;
  handleSkip: () => void;
  handleReset: () => void;
  handleTabChange: (tab: string) => void;
  getTimerDuration: (tab: string) => number;
}

interface TimerProviderProps {
  children: ReactNode;
  settings: Settings;
  currentTaskId: string | null;
  onSessionComplete: (
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    nextSessionType: SessionType
  ) => void;
  onSessionInterrupt: (
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    actualDuration: number
  ) => void;
  onSessionSkip: (
    sessionId: string,
    sessionType: SessionType,
    startTime: number,
    endTime: number,
    plannedDuration: number,
    actualDuration: number
  ) => void;
  onPause: () => void;
  onResume: (tab: string) => void;
  onReset: () => void;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider: React.FC<TimerProviderProps> = ({
  children,
  settings,
  currentTaskId,
  onSessionComplete,
  onSessionInterrupt,
  onSessionSkip,
  onPause,
  onResume,
  onReset,
}) => {
  const [recovered] = useState<RecoveredSession>(() => recoverSession(settings));

  const [currentTab, setCurrentTab] = useState<string>(
    recovered?.kind === 'active' ? recovered.tab :
    recovered?.kind === 'completed' ? recovered.nextTab :
    'pomodoro'
  );
  const [timeLeft, setTimeLeft] = useState<number>(
    recovered?.kind === 'active' ? recovered.timeLeft :
    recovered?.kind === 'completed' ? getDurationForType(recovered.nextTab, settings) :
    settings.workDuration * 60
  );
  const [isActive, setIsActive] = useState(false);
  const [pomodoroCount, setPomodoroCount] = useState(0);
  const [sessionStarted, setSessionStarted] = useState(recovered?.kind === 'active');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(
    recovered?.kind === 'active' ? recovered.sessionId : null
  );
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(
    recovered?.kind === 'active' ? recovered.sessionStartTime : null
  );
  const [timerStartTime, setTimerStartTime] = useState<number | null>(null);
  const [initialTimeLeft, setInitialTimeLeft] = useState<number>(
    recovered?.kind === 'active' ? recovered.timeLeft :
    recovered?.kind === 'completed' ? getDurationForType(recovered.nextTab, settings) :
    settings.workDuration * 60
  );

  const autoStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs so interval callback always sees latest values without re-subscribing
  const settingsRef = useRef(settings);
  const currentTaskIdRef = useRef(currentTaskId);
  const onSessionCompleteRef = useRef(onSessionComplete);
  const onSessionInterruptRef = useRef(onSessionInterrupt);
  const onSessionSkipRef = useRef(onSessionSkip);
  const onPauseRef = useRef(onPause);
  const onResumeRef = useRef(onResume);
  const onResetRef = useRef(onReset);

  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { currentTaskIdRef.current = currentTaskId; }, [currentTaskId]);
  useEffect(() => { onSessionCompleteRef.current = onSessionComplete; }, [onSessionComplete]);
  useEffect(() => { onSessionInterruptRef.current = onSessionInterrupt; }, [onSessionInterrupt]);
  useEffect(() => { onSessionSkipRef.current = onSessionSkip; }, [onSessionSkip]);
  useEffect(() => { onPauseRef.current = onPause; }, [onPause]);
  useEffect(() => { onResumeRef.current = onResume; }, [onResume]);
  useEffect(() => { onResetRef.current = onReset; }, [onReset]);

  // Activate the timer immediately on mount if a session was recovered
  useEffect(() => {
    if (recovered?.kind === 'active') {
      setTimerStartTime(performance.now());
      setIsActive(true);
    }
  }, [recovered]);

  const getTimerDuration = useCallback((tab: string): number => {
    const s = settingsRef.current;
    switch (tab) {
      case 'pomodoro':    return s.workDuration * 60;
      case 'shortBreak':  return s.shortBreakDuration * 60;
      case 'longBreak':   return s.longBreakDuration * 60;
      default:            return s.workDuration * 60;
    }
  }, []);

  const syncTimerWithRealTime = useCallback(() => {
    setTimerStartTime(prev => {
      if (prev === null) return prev;
      const elapsed = Math.floor((performance.now() - prev) / 1000);
      setInitialTimeLeft(init => {
        const corrected = Math.max(0, init - elapsed);
        setTimeLeft(corrected);
        return init;
      });
      return prev;
    });
  }, []);

  const handleVisibilityChange = useCallback(() => {
    if (!document.hidden) {
      syncTimerWithRealTime();
    }
  }, [syncTimerWithRealTime]);

  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [handleVisibilityChange]);

  // 100 ms tick — drives countdown and auto-start logic
  useEffect(() => {
    if (!isActive || timerStartTime === null) return;

    const interval = setInterval(() => {
      const elapsed = Math.floor((performance.now() - timerStartTime) / 1000);
      const newTimeLeft = Math.max(0, initialTimeLeft - elapsed);
      setTimeLeft(newTimeLeft);

      if (newTimeLeft === 0) {
        setIsActive(false);
        setTimerStartTime(null);

        const endTime = Date.now();
        const snap = {
          sessionId: currentSessionId!,
          sessionType: currentTab as SessionType,
          startTime: sessionStartTime!,
          plannedDuration: initialTimeLeft,
        };

        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'timer_complete', {
            event_category: 'pomodoro_timer',
            event_label: snap.sessionType === 'pomodoro' ? 'work_session' : 'break_session',
            session_type: snap.sessionType,
            session_duration: snap.plannedDuration,
          });
        }

        // Compute next session before firing callback so we can pass it along
        let nextTab: string;
        if (currentTab === 'pomodoro') {
          const nextCount = pomodoroCount + 1;
          setPomodoroCount(nextCount);
          nextTab = nextCount % settingsRef.current.longBreakInterval === 0 ? 'longBreak' : 'shortBreak';
        } else {
          nextTab = 'pomodoro';
        }

        onSessionCompleteRef.current(
          snap.sessionId,
          snap.sessionType,
          snap.startTime,
          endTime,
          snap.plannedDuration,
          nextTab as SessionType
        );

        setSessionStarted(false);
        setCurrentSessionId(null);
        setSessionStartTime(null);
        clearCurrentSession();

        const nextDuration = getTimerDuration(nextTab);
        setCurrentTab(nextTab);
        setTimeLeft(nextDuration);
        setInitialTimeLeft(nextDuration);

        const shouldAutoStart =
          nextTab === 'pomodoro'
            ? settingsRef.current.autoStartWork
            : settingsRef.current.autoStartBreaks;

        if (shouldAutoStart) {
          autoStartTimeoutRef.current = setTimeout(() => {
            const sessionId = getNextSessionId(nextTab as SessionType);
            const startTime = Date.now();
            setCurrentSessionId(sessionId);
            setSessionStartTime(startTime);
            setSessionStarted(true);
            setIsActive(true);
            setTimerStartTime(performance.now());
            Storage.write('kf-session-state', {
              currentSessionId: sessionId,
              sessionType: nextTab,
              startTime,
              timeLeft: nextDuration,
              wallClockStart: startTime,
            });
            onResumeRef.current(nextTab);
          }, 1500);
        }
      }
    }, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isActive, timerStartTime, initialTimeLeft, currentTab, pomodoroCount, currentSessionId, sessionStartTime, getTimerDuration]);

  const handleStartStop = useCallback(() => {
    if (!isActive && !sessionStarted) {
      // Start new session — cancel any pending auto-start countdown
      if (autoStartTimeoutRef.current) clearTimeout(autoStartTimeoutRef.current);
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        ensureNotificationPermission();
      }

      const sessionType = currentTab as SessionType;
      const sessionId = getNextSessionId(sessionType);
      const startTime = Date.now();

      if (typeof window.gtag !== 'undefined') {
        window.gtag('event', 'timer_start', {
          event_category: 'pomodoro_timer',
          event_label: sessionType === 'pomodoro' ? 'work_session' : 'break_session',
          session_count: pomodoroCount,
          session_type: sessionType,
        });
      }

      setCurrentSessionId(sessionId);
      setSessionStartTime(startTime);
      setSessionStarted(true);
      setTimerStartTime(performance.now());
      setIsActive(true);

      Storage.write('kf-session-state', {
        currentSessionId: sessionId,
        sessionType,
        startTime,
        timeLeft: getTimerDuration(currentTab),
        wallClockStart: startTime,
      });

      if (currentTaskId && sessionType === 'pomodoro') {
        onResumeRef.current(currentTab);
      }
    } else if (isActive) {
      // Pause
      onPauseRef.current();
      setTimerStartTime(null);
      setIsActive(false);
    } else {
      // Resume
      const now = performance.now();
      const adjustedStart = now - (initialTimeLeft - timeLeft) * 1000;
      setTimerStartTime(adjustedStart);
      setIsActive(true);

      if (currentTaskId && currentTab === 'pomodoro') {
        onResumeRef.current(currentTab);
      }
    }
  }, [isActive, sessionStarted, currentTab, pomodoroCount, currentTaskId, initialTimeLeft, timeLeft, getTimerDuration]);

  const handleTabChange = useCallback((tab: string) => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'tab_change', {
        event_category: 'pomodoro_timer',
        event_label: `${currentTab}_to_${tab}`,
        from_tab: currentTab,
        to_tab: tab,
      });
    }

    if (currentSessionId && sessionStartTime && sessionStarted) {
      const endTime = Date.now();
      const plannedDuration = getTimerDuration(currentTab);
      const actualDuration = plannedDuration - timeLeft;
      onSessionInterruptRef.current(
        currentSessionId,
        currentTab as SessionType,
        sessionStartTime,
        endTime,
        plannedDuration,
        actualDuration
      );
    }

    onPauseRef.current();
    if (autoStartTimeoutRef.current) clearTimeout(autoStartTimeoutRef.current);

    const newDuration = getTimerDuration(tab);
    setCurrentTab(tab);
    setTimeLeft(newDuration);
    setInitialTimeLeft(newDuration);
    setIsActive(false);
    setTimerStartTime(null);
    setSessionStarted(false);
    setCurrentSessionId(null);
    setSessionStartTime(null);
    clearCurrentSession();
  }, [currentTab, currentSessionId, sessionStartTime, sessionStarted, timeLeft, getTimerDuration]);

  const handleSkip = useCallback(() => {
    if (!sessionStarted) return;

    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'session_skipped', {
        event_category: 'pomodoro_timer',
        session_type: currentTab,
        time_remaining_sec: timeLeft,
      });
    }

    if (currentSessionId && sessionStartTime) {
      const endTime = Date.now();
      const plannedDuration = getTimerDuration(currentTab);
      const actualDuration = plannedDuration - timeLeft;
      onSessionSkipRef.current(
        currentSessionId,
        currentTab as SessionType,
        sessionStartTime,
        endTime,
        plannedDuration,
        actualDuration
      );
    }

    onPauseRef.current();

    // Compute next session outside the updater so nextTab is available for auto-start
    let nextTab: string;
    if (currentTab === 'pomodoro') {
      const nextCount = pomodoroCount + 1;
      setPomodoroCount(nextCount);
      nextTab = nextCount % settingsRef.current.longBreakInterval === 0 ? 'longBreak' : 'shortBreak';
    } else {
      nextTab = 'pomodoro';
    }
    const nextDuration = getTimerDuration(nextTab);

    setCurrentTab(nextTab);
    setTimeLeft(nextDuration);
    setInitialTimeLeft(nextDuration);
    setIsActive(false);
    setTimerStartTime(null);
    setSessionStarted(false);
    setCurrentSessionId(null);
    setSessionStartTime(null);
    clearCurrentSession();

    const shouldAutoStart =
      nextTab === 'pomodoro'
        ? settingsRef.current.autoStartWork
        : settingsRef.current.autoStartBreaks;

    if (shouldAutoStart) {
      if (autoStartTimeoutRef.current) clearTimeout(autoStartTimeoutRef.current);
      autoStartTimeoutRef.current = setTimeout(() => {
        const sessionId = getNextSessionId(nextTab as SessionType);
        const startTime = Date.now();
        setCurrentSessionId(sessionId);
        setSessionStartTime(startTime);
        setSessionStarted(true);
        setIsActive(true);
        setTimerStartTime(performance.now());
        Storage.write('kf-session-state', {
          currentSessionId: sessionId,
          sessionType: nextTab,
          startTime,
          timeLeft: nextDuration,
          wallClockStart: startTime,
        });
        onResumeRef.current(nextTab);
      }, 1500);
    }
  }, [sessionStarted, currentTab, timeLeft, currentSessionId, sessionStartTime, pomodoroCount, getTimerDuration]);

  const handleReset = useCallback(() => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'timer_reset', {
        event_category: 'pomodoro_timer',
        event_label: 'timer_reset',
        previous_session_count: pomodoroCount,
      });
    }

    if (currentSessionId && sessionStartTime && sessionStarted) {
      const endTime = Date.now();
      const plannedDuration = getTimerDuration(currentTab);
      const actualDuration = plannedDuration - timeLeft;
      onSessionInterruptRef.current(
        currentSessionId,
        currentTab as SessionType,
        sessionStartTime,
        endTime,
        plannedDuration,
        actualDuration
      );
    }

    onResetRef.current();
    if (autoStartTimeoutRef.current) clearTimeout(autoStartTimeoutRef.current);

    const pomoDuration = settings.workDuration * 60;
    setCurrentTab('pomodoro');
    setPomodoroCount(0);
    setIsActive(false);
    setTimerStartTime(null);
    setSessionStarted(false);
    setCurrentSessionId(null);
    setSessionStartTime(null);
    setTimeLeft(pomoDuration);
    setInitialTimeLeft(pomoDuration);
    clearCurrentSession();
  }, [pomodoroCount, currentSessionId, sessionStartTime, sessionStarted, currentTab, timeLeft, getTimerDuration, settings.workDuration]);

  // Keep timer duration in sync when settings change.
  // Idle: update immediately. Running/paused: clamp down if new duration is shorter than remaining time.
  useEffect(() => {
    const newDuration = getTimerDuration(currentTab);
    if (!isActive && !sessionStarted) {
      setTimeLeft(newDuration);
      setInitialTimeLeft(newDuration);
    } else {
      setTimeLeft(prev => {
        if (newDuration < prev) {
          setInitialTimeLeft(newDuration);
          if (isActive) setTimerStartTime(performance.now());
          return newDuration;
        }
        return prev;
      });
    }
  }, [settings, currentTab, getTimerDuration, isActive, sessionStarted]);

  return (
    <TimerContext.Provider value={{
      timeLeft,
      isActive,
      initialTimeLeft,
      sessionStarted,
      currentSessionId,
      sessionStartTime,
      pomodoroCount,
      currentTab,
      handleStartStop,
      handleSkip,
      handleReset,
      handleTabChange,
      getTimerDuration,
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = (): TimerContextType => {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within a TimerProvider');
  return ctx;
};
